<script lang="ts">
    import { _ } from "svelte-i18n"
    import { kirbyFetch } from "../../api/kirby/api"
    import { getPageSelect } from "../../api/kirby/queries"
    import { currentPage, lang, location } from "../../store"
    import { fallbackLocale } from "../../i18n"
    import HeroSlider from "../pagebuilder/blocks/heroSlider/HeroSlider.svelte"
    import MainLayout from "../pagebuilder/MainLayout.svelte"
    import MiniLoader from "../widgets/MiniLoader.svelte"
    import SectionTitleWrapper from "../widgets/SectionTitleWrapper.svelte"
    import NotFound from "./NotFound.svelte"
    import { kirbyUrl, unlistedToken } from "../../../../api/hooks/config-client"
    import Index from "../SEO/Index.svelte"
    import { flattenBlocksForMainLayout } from "./helper"
    import { onDestroy, tick } from "svelte"
    import { spaNavigate } from "../../functionTools/routing"

    let { path }: { path: string } = $props()
    let loading = $state(true)

    let page: KirbyPage<"any"> = $state()
    let isArticle = $state(false)
    let isPerson = $state(false)

    function toBoolean(value: unknown): boolean {
        return value === true || value === "true" || value === 1 || value === "1"
    }

    function stripLangFromPath(currentPath: string): string {
        return currentPath.replace(/^\/[a-z]{2}(?=\/|$)/, "")
    }

    function extractSlug(currentPath: string): string {
        const withoutLang = currentPath.replace(/^\/[a-z]{2}\//, "").replace(/\/+$/, "")
        return withoutLang.replace(/^\//, "")
    }

    let loadedPath = $state("")
    let loadSequence = 0

    async function loadPage(currentPath: string, currentLanguage: string) {
        const sequence = ++loadSequence
        loading = true
        isArticle = false
        isPerson = false
        currentPage.set(null)

        const pathWithoutLang = stripLangFromPath(currentPath)
        const slug = extractSlug(currentPath)
        const preferredLanguage = currentLanguage || fallbackLocale

        const query = `page('${slug}')`
        let effectiveLanguage = preferredLanguage

        let res = await kirbyFetch<KirbyPage<"any">>({
            query,
            select: getPageSelect(),
            language: preferredLanguage,
        })

        if (!res && preferredLanguage !== fallbackLocale) {
            res = await kirbyFetch<KirbyPage<"any">>({
                query,
                select: getPageSelect(),
                language: fallbackLocale,
            })
            effectiveLanguage = fallbackLocale
        }

        if (sequence !== loadSequence) return

        loading = false
        if (!res) return
        if ($location.search.includes("preview=true") && $location.search.includes("pw=" + unlistedToken)) void 0
        else if (res.status !== "listed" && res.status !== "unlisted") return

        const needsFallbackLanguage =
            preferredLanguage !== fallbackLocale &&
            (toBoolean(res.no_en_version) || effectiveLanguage === fallbackLocale)

        if (needsFallbackLanguage) {
            $lang = fallbackLocale
            const targetPath = `/${fallbackLocale}${pathWithoutLang || ""}`
            if ($location.path !== targetPath) {
                spaNavigate(targetPath, { replace: true, corrected: true })
                return
            }
        }

        currentPage.set({
            id: res.id,
            url: res.url,
            no_en_version: res.no_en_version,
        })
        page = res
        if (res.intendedTemplate.includes("article")) isArticle = true
        if (res.intendedTemplate.includes("person")) isPerson = true
        if (typeof window !== "undefined") {
            tick().then(() => {
                const contentPage = document.querySelector("#contentpage") as HTMLElement | null
                if (!contentPage) return

                const sideNav = contentPage.querySelector(".sticky-col .inner-wrapper .upper") as HTMLElement | null
                let height = sideNav?.offsetHeight || 0
                const lower = contentPage.querySelector(".sticky-col .inner-wrapper .lower") as HTMLElement | null
                height += lower?.offsetHeight || 0
                height += 96 // Abstand u. puffer

                if (contentPage.offsetHeight < height) {
                    contentPage.style.minHeight = height + "px"
                }
            })
        }
    }

    if (typeof window === "undefined") {
        const currentPath = path
        const currentLanguage = String($lang || fallbackLocale)
        if (currentPath) {
            loadedPath = `${currentLanguage}:${currentPath}`
            void loadPage(currentPath, currentLanguage)
        }
    }

    $effect(() => {
        const currentPath = path
        const currentLanguage = String($lang || fallbackLocale)
        const loadKey = `${currentLanguage}:${currentPath}`
        if (!currentPath || loadedPath === loadKey) return
        loadedPath = loadKey
        void loadPage(currentPath, currentLanguage)
    })

    onDestroy(() => {
        currentPage.set(null)
    })

    let stickyCol: HTMLElement = $state()
    function formatId(id: string | string[]) {
        if (Array.isArray(id)) id = id[0]
        if (!id) return

        const match = id.match(/(['"])file:\/\/([\w-]+)\1/)
        const formattedId = match ? match[2] : id?.replace("file://", "")

        return formattedId
    }
</script>

{#snippet stickyUpperContent(mobile = false)}
    {@const blocks = flattenBlocksForMainLayout(page.sticky_upper_layout)}
    {#if blocks?.length}
        <div class={mobile ? "upper-mobile" : "upper"}>
            {#each blocks as block}
                <MainLayout {block} pageId={page.uuid} pageTemplate={page.intendedTemplate} />
            {/each}
        </div>
    {/if}
{/snippet}
{#snippet stickyLowerContent(mobile = false)}
    {@const blocks = flattenBlocksForMainLayout(page.sticky_lower_layout)}
    {#if blocks?.length}
        <div class={mobile ? "lower-mobile" : "lower"}>
            {#each blocks as block}
                <MainLayout {block} pageId={page.uuid} pageTemplate={page.intendedTemplate} />
            {/each}
        </div>
    {/if}
{/snippet}
{#if loading}
    <MiniLoader />
{:else if page}
    <Index
        title={page.meta_title || page.title}
        metaDescription={page.meta_description}
        noIndex={page.meta_noindex}
        imageUrl={page.meta_img ? `${kirbyUrl}thumbs/${formatId(page.meta_img)}` : null}
    />
    {#if page.hero_row_toggle}
        {#each page.hero_row as layout, i}
            <MainLayout
                rowIdx={i}
                block={layout.columns[0].blocks[0]}
                pageId={page.uuid}
                pageTemplate={page.intendedTemplate}
            />
        {/each}
    {:else if isArticle}
        <HeroSlider slides={[{ ...page } as unknown as Slide]} publication_date={(page as any)?.publication_date} />
    {/if}

    <main class="contentpage" id="contentpage">
        <SectionTitleWrapper
            type={isArticle || isPerson ? "return" : "title"}
            {isArticle}
            article={page}
            title={page.sticky_title_header}
            referenceTitle={page.sticky_title_header || $_("general.back")}
            page="/{isPerson
                ? $lang == 'de'
                    ? 'vorstand'
                    : 'executive-board'
                : page.id.split('/').slice(0, -1).join('/')}"
            {isPerson}
            enabled={page.sticky_title_header_toggle || isArticle}
            breadcrumbsForMobile={true}
        >
            {#snippet content()}
                {@const mainBlocks = flattenBlocksForMainLayout(page.main_layout)}
                {#if mainBlocks?.length > 0}
                    <section class="splitted-layout">
                        <div class="main-col">
                            {#each mainBlocks as block, idx}
                                <div class="inner-wrapper">
                                    <div class="max-width-wrapper">
                                        <MainLayout {block} rowIdx={idx + 1} pageTemplate={page.intendedTemplate} />
                                    </div>
                                </div>
                            {/each}
                        </div>
                        <div class="sticky-col" bind:this={stickyCol}>
                            <div
                                class="inner-wrapper"
                                class:additionTop={page.sticky_title_header_toggle || isArticle || isPerson}
                            >
                                {@render stickyUpperContent()}
                                {@render stickyLowerContent()}
                            </div>
                        </div>
                    </section>
                {/if}
            {/snippet}
        </SectionTitleWrapper>

        <section class="lower-row">
            {@render stickyUpperContent(true)}
            {@render stickyLowerContent(true)}
            {#each flattenBlocksForMainLayout(page.bottom_content) as block, idx}
                <MainLayout {block} rowIdx={idx} pageTemplate={page.intendedTemplate} />
            {/each}
        </section>
    </main>
{:else}
    <NotFound />
{/if}

<style lang="less">
    @import "../../css/variables.less";
    .contentpage {
        .splitted-layout {
            display: flex;
            width: 100%;
            justify-content: center;
            position: relative;
            min-height: max(100%, 700px);
            .main-col {
                width: 100%;
                display: flex;
                flex-direction: column;
                align-items: center;
                position: relative;
                padding: 0px var(--default-padding);

                .inner-wrapper {
                    width: 100%;
                    max-width: var(--extended-max-width);
                    height: fit-content;
                    .max-width-wrapper {
                        max-width: min(var(--max-column-width), calc(100% - var(--max-sidebar-width) - 2.4rem));
                        @media (max-width: 1400px) {
                            max-width: unset;
                        }
                    }
                }
            }

            @media (max-width: 1400px) {
                flex-direction: column-reverse;
                gap: 2.4rem;
                .sticky-col {
                    position: relative;
                    right: 0;
                    max-width: unset;
                    min-height: 100%;
                    width: 100%;
                    top: 0px;
                    padding: 0px var(--default-padding);
                    .inner-wrapper {
                        position: relative;
                        top: 0 !important;
                        height: fit-content;
                        .lower {
                            display: none;
                        }
                    }
                }
            }
        }
        .lower-row {
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 100%;
            .lower-mobile,
            .upper-mobile {
                @media (min-width: 1400px) {
                    display: none;
                }
                padding: 0px var(--default-padding);
                width: 100%;
                margin-top: 2.4rem;
            }
        }
        .sticky-col {
            height: 100%;
            width: 100%;
            position: absolute;
            max-width: var(--max-sidebar-width);
            display: flex;
            justify-content: center;
            right: max(calc(calc((100vw - var(--extended-max-width)) / 2)), var(--default-padding));

            z-index: 2;
            .inner-wrapper {
                max-width: var(--extended-max-width);
                width: 100%;
                position: sticky;
                height: calc(100vh - var(--header-height) - 3rem - var(--default-padding));
                max-height: 100%;
                min-height: 0; /* Flex-Kind darf schrumpfen für overflow */
                overflow-y: auto; /* Inhalt separat scrollbar */
                overflow-x: hidden;
                gap: 2.4rem;
                justify-content: space-between;
                display: flex;
                flex-direction: column;
                top: calc(var(--header-height));
                align-items: flex-end;
                /* Scrollbar ausblenden, Scrollen bleibt möglich */
                scrollbar-width: none;
                -ms-overflow-style: none;
                &::-webkit-scrollbar {
                    display: none;
                }
                &.additionTop {
                    top: calc(var(--header-height) + 3rem);
                }
                .upper {
                    width: 100%;
                    height: fit-content;
                }
                .lower {
                    width: 100%;
                    height: fit-content;
                }
            }
        }
    }
</style>
